import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  TestChatBadge,
  InspectorDrawer,
  DeleteThreadModal,
  MessageInput,
  TypingIndicator,
  MessageTraceIndicator,
} from '@/components/chat';
import { useChatThread, usePollingMessages } from '@/hooks/useChatThreads';
import { useSendTestMessage } from '@/hooks/useSendTestMessage';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-handler';
import { formatDateTime, formatTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { Message } from '@/types/chat';

export function ChatDetailPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();

  const [lastMessageId, setLastMessageId] = useState<string | undefined>(undefined);
  const [inspectorOpen, setInspectorOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isWaitingForAgent, setIsWaitingForAgent] = useState(false);
  const [shouldAutoSelectLast, setShouldAutoSelectLast] = useState(true);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const waitingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const agentMessageCountRef = useRef<number>(0);
  const sendTestMessage = useSendTestMessage();
  const { error: toastError } = useToast();

  // Initial thread detail (includes first N messages) — also polls at 2s to capture OUTGOING
  const {
    data: thread,
    isLoading: threadLoading,
    error: threadError,
    isFetching: threadFetching,
    refetch: refetchThread,
  } = useChatThread(threadId ?? '');

  // Polling for new messages every 2s
  const { data: pollingData, refetch: refetchMessages, isFetching: messagesFetching } =
    usePollingMessages(threadId ?? '', lastMessageId);

  // Keep track of newest message id for cursor-based polling
  useEffect(() => {
    const newMessages = pollingData?.messages;
    if (newMessages && newMessages.length > 0) {
      const last = newMessages[newMessages.length - 1];
      setLastMessageId(last.id);
    }
  }, [pollingData]);

  const allMessages = useMemo<Message[]>(() => {
    const initial = thread?.messages ?? [];
    const polled = pollingData?.messages ?? [];
    const initialIds = new Set(initial.map((m) => m.id));
    const uniquePolled = polled.filter((m) => !initialIds.has(m.id));
    return [...initial, ...uniquePolled];
  }, [thread?.messages, pollingData?.messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length]);

  useEffect(() => {
    if (!isWaitingForAgent) return;
    const currentCount = allMessages.filter(
      (m) => m.direction === 'OUTGOING' && m.role?.toUpperCase() === 'AI',
    ).length;
    if (currentCount > agentMessageCountRef.current) {
      if (waitingTimerRef.current) clearTimeout(waitingTimerRef.current);
      setIsWaitingForAgent(false);
    }
  }, [allMessages, isWaitingForAgent]);

  // Auto-select last AI message when opening inspector for first time
  useEffect(() => {
    if (inspectorOpen && shouldAutoSelectLast && !selectedMessage) {
      const lastAIMessage = [...allMessages]
        .reverse()
        .find((m) => m.direction === 'OUTGOING' && m.role?.toUpperCase() === 'AI');
      if (lastAIMessage) {
        setSelectedMessage(lastAIMessage);
        setShouldAutoSelectLast(false);
      }
    }
  }, [inspectorOpen, shouldAutoSelectLast, selectedMessage, allMessages]);

  function handleMessageClick(message: Message) {
    // All messages (INCOMING + AI OUTGOING) are inspectable.
    // Drawer adapts its content based on direction/role.
    setSelectedMessage(message);
    setInspectorOpen(true);
  }

  // ---- Render states ----
  if (threadLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (threadError) {
    return (
      <div className="flex items-center gap-2 text-destructive py-10">
        <AlertCircle className="h-5 w-5" />
        <span className="text-sm">Erro ao carregar conversa. Tente novamente.</span>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="py-10 text-center text-muted-foreground text-sm">
        Conversa não encontrada.
      </div>
    );
  }

  const isTestChat = thread.isTestChat ?? false;

  const isRefreshing = threadFetching || messagesFetching;

  function handleRefresh() {
    void refetchThread();
    void refetchMessages();
  }

  function handleSend(message: string) {
    if (!threadId || !isTestChat) return;

    agentMessageCountRef.current = allMessages.filter(
      (m) => m.direction === 'OUTGOING' && m.role?.toUpperCase() === 'AI',
    ).length;
    if (waitingTimerRef.current) clearTimeout(waitingTimerRef.current);
    setIsWaitingForAgent(true);
    waitingTimerRef.current = setTimeout(() => setIsWaitingForAgent(false), 30_000);

    sendTestMessage.mutate(
      { threadId, content: message },
      {
        onError: (err) => {
          if (waitingTimerRef.current) clearTimeout(waitingTimerRef.current);
          setIsWaitingForAgent(false);
          toastError(getErrorMessage(err, 'Erro ao enviar mensagem'));
        },
      },
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b mb-4">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/chat')}
            aria-label="Voltar para conversas"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Conversas
          </Button>

          <span className="font-mono text-sm font-medium">{thread.phoneNumber}</span>

          <TestChatBadge isTestChat={isTestChat} />
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Última mensagem: {formatDateTime(thread.lastMessageAt)}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar mensagens"
            aria-label="Atualizar mensagens"
          >
            <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeleteModalOpen(true)}
            className="text-destructive border-destructive/40 hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Excluir
          </Button>
        </div>
      </div>

      {/* Message list — scrollable */}
      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {allMessages.length === 0 && (
          <p className="text-center text-sm text-muted-foreground py-10">
            Nenhuma mensagem encontrada
          </p>
        )}

        {allMessages.map((message) => {
          const isIncoming = message.direction === 'INCOMING';
          const isAI =
            message.direction === 'OUTGOING' && message.role?.toUpperCase() === 'AI';
          const isClickable = isIncoming || isAI;

          return (
            <div
              key={message.id}
              className={cn('flex', isIncoming ? 'justify-start' : 'justify-end')}
            >
              <div className="max-w-[80%] sm:max-w-[65%]">
                {/* Role label */}
                <p
                  className={cn(
                    'text-xs text-muted-foreground mb-1',
                    isIncoming ? 'text-left' : 'text-right',
                  )}
                >
                  {message.role?.toUpperCase() === 'AI'
                    ? 'Agente IA'
                    : message.role?.toUpperCase() === 'AGENT'
                    ? 'Atendente'
                    : 'Contato'}
                </p>

                {/* Bubble */}
                <div
                  className={cn(
                    'rounded-lg border px-4 py-2 text-sm',
                    isIncoming
                      ? 'bg-muted text-foreground border-border'
                      : isAI
                      ? 'bg-primary/90 text-primary-foreground border-primary/40'
                      : 'bg-secondary text-secondary-foreground border-secondary/50',
                    isClickable &&
                      'cursor-pointer transition-colors hover:border-primary/60 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                  )}
                  onClick={isClickable ? () => handleMessageClick(message) : undefined}
                  role={isClickable ? 'button' : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  onKeyDown={
                    isClickable
                      ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleMessageClick(message);
                          }
                        }
                      : undefined
                  }
                  aria-label={
                    isClickable
                      ? isAI
                        ? 'Inspecionar trace do agente'
                        : 'Inspecionar payload da mensagem'
                      : undefined
                  }
                  title={isClickable ? 'Clique para inspecionar' : undefined}
                >
                  <p className="whitespace-pre-wrap break-words">{message.content}</p>

                  {/* Timestamp */}
                  <p
                    className={cn(
                      'text-xs mt-1',
                      isIncoming ? 'text-left opacity-60' : 'text-right opacity-70',
                    )}
                  >
                    {formatTime(message.createdAt)}
                  </p>
                </div>

                {/* Trace indicator for AI messages */}
                {isAI && (
                  <MessageTraceIndicator
                    message={message}
                    onInspect={() => {
                      setSelectedMessage(message);
                      setInspectorOpen(true);
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {isWaitingForAgent && <TypingIndicator />}

        {/* Sentinel for auto-scroll */}
        <div ref={messagesEndRef} />
      </div>

      {/* Message input — always disabled in SF01 for real chats */}
      <div className="pt-4 border-t mt-4">
        <MessageInput
          disabled={!isTestChat}
          placeholder="Enviar mensagem de teste..."
          onSend={isTestChat ? handleSend : undefined}
        />
      </div>

      {/* Inspector Drawer */}
      {threadId && (
        <InspectorDrawer
          open={inspectorOpen}
          onClose={() => {
            setInspectorOpen(false);
            setSelectedMessage(null);
            setShouldAutoSelectLast(true);
          }}
          threadId={threadId}
          messageId={selectedMessage?.id ?? null}
          message={selectedMessage}
        />
      )}

      {/* Delete Thread Modal */}
      {threadId && (
        <DeleteThreadModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          threadId={threadId}
          onSuccess={() => navigate('/chat')}
        />
      )}
    </div>
  );
}
